import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { notify } from '../utils/notificationCenter';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  public api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 180000, // 3 minutos para importaciones grandes (300+ usuarios)
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor para agregar token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor para manejar errores y refresh token
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Extraer mensaje amigable
        const status = error.response?.status;
        const backendMessage = error.response?.data?.message || error.message;
        const url = originalRequest?.url;

        // Notificación de error genérica inmediato (excepto manejo especial 401 con refresh)
        if (status && status !== 401) {
          notify({
            type: 'error',
            priority: status >= 500 ? 'critical' : 'high',
            title: status >= 500 ? 'Error del servidor' : 'Error en la solicitud',
            message: `${backendMessage}${url ? ` (endpoint: ${url})` : ''}`.slice(0,300),
            autoClose: true,
            duration: 8
          });
        } else if (!status) {
          // Error de red / timeout
          notify({
            type: 'warning',
            priority: 'high',
            title: 'Problema de conexión',
            message: 'No se pudo contactar al servidor. Verifica tu conexión a internet.',
            autoClose: true,
            duration: 8
          });
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              const { token } = response.data.tokens;
              
              localStorage.setItem('accessToken', token);
              originalRequest.headers.Authorization = `Bearer ${token}`;
              
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Si el refresh falla, limpiar tokens y redirigir al login
            this.clearTokens();
            notify({
              type: 'error',
              priority: 'high',
              title: 'Sesión expirada',
              message: 'Tu sesión ha expirado. Inicia sesión nuevamente.',
              autoClose: true,
              duration: 6
            });
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(refreshToken: string): Promise<AxiosResponse> {
    return this.api.post('/auth/refresh', { refreshToken });
  }

  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // Métodos de autenticación
  async login(credentials: { email: string; password: string; twoFactorCode?: string }) {
    return this.api.post('/auth/login', credentials);
  }

  async register(userData: any) {
    return this.api.post('/auth/register', userData);
  }

  async logout() {
    const refreshToken = localStorage.getItem('refreshToken');
    const response = await this.api.post('/auth/logout', { refreshToken });
    this.clearTokens();
    return response;
  }

  async forgotPassword(email: string) {
    return this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string) {
    return this.api.post('/auth/reset-password', { token, password });
  }

  async verifyEmail(token: string) {
    return this.api.post('/auth/verify-email', { token });
  }

  async enable2FA() {
    return this.api.post('/auth/2fa/enable');
  }

  async verify2FA(token: string) {
    return this.api.post('/auth/2fa/verify', { token });
  }

  // Métodos de usuario
  async getCurrentUser() {
    return this.api.get('/users/me');
  }

  async getUserProfile() {
    return this.api.get('/auth/profile');
  }

  async updateProfile(data: any) {
    return this.api.put('/users/me', data);
  }

  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return this.api.put('/auth/change-password', data);
  }

  async getUsers(params?: { page?: number; limit?: number; search?: string; role?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    
    const endpoint = `/admin/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    return this.api.get(endpoint);
  }

  async getUserById(id: string) {
    return this.api.get(`/users/${id}`);
  }

  async createUser(userData: any) {
    return this.api.post('/users', userData);
  }

  async updateUser(id: string, userData: any) {
    return this.api.put(`/users/${id}`, userData);
  }

  async deleteUser(id: string) {
    return this.api.delete(`/users/${id}`);
  }

  async getComplexes() {
    return this.api.get('/complexes');
  }

  // Métodos de asambleas
  async getAssemblies(params?: any) {
    return this.api.get('/assemblies', { params });
  }

  async getAssemblyById(id: string) {
    return this.api.get(`/assemblies/${id}`);
  }

  async createAssembly(assemblyData: any) {
    return this.api.post('/assemblies', assemblyData);
  }

  async updateAssembly(id: string, assemblyData: any) {
    return this.api.put(`/assemblies/${id}`, assemblyData);
  }

  async deleteAssembly(id: string) {
    return this.api.delete(`/assemblies/${id}`);
  }

  async startAssembly(id: string) {
    return this.api.post(`/assemblies/${id}/start`);
  }

  async endAssembly(id: string) {
    return this.api.post(`/assemblies/${id}/end`);
  }

  async joinAssembly(id: string) {
    return this.api.post(`/assemblies/${id}/join`);
  }

  async leaveAssembly(id: string) {
    return this.api.post(`/assemblies/${id}/leave`);
  }

  async resendAssemblyInvitations(id: string) {
    return this.api.post(`/assemblies/${id}/resend-invitations`);
  }

  async sendAssemblyReminders(id: string) {
    return this.api.post(`/assemblies/${id}/send-reminders`);
  }

  // Métodos de votaciones
  async createVotingSession(votingData: any) {
    return this.api.post('/voting/sessions', votingData);
  }

  async updateVotingSession(sessionId: string, votingData: any) {
    return this.api.put(`/voting/sessions/${sessionId}`, votingData);
  }

  async deleteVotingSession(sessionId: string) {
    return this.api.delete(`/voting/sessions/${sessionId}`);
  }

  async getVotingSession(sessionId: string) {
    return this.api.get(`/voting/sessions/${sessionId}`);
  }

  async getVotingSessionsByAssembly(assemblyId: string) {
    return this.api.get(`/voting/assemblies/${assemblyId}/sessions`);
  }

  async getVotingSessionsManagerView(assemblyId: string) {
    return this.api.get(`/voting/assemblies/${assemblyId}/sessions/manager-view`);
  }

  async getVotingSessionsLiveView(assemblyId: string) {
    return this.api.get(`/voting/assemblies/${assemblyId}/sessions/live-view`);
  }

  async getVotingSessionsHistoryView(assemblyId: string) {
    return this.api.get(`/voting/assemblies/${assemblyId}/sessions/history-view`);
  }

  async startVoting(sessionId: string) {
    return this.api.post(`/voting/sessions/${sessionId}/start`);
  }

  async endVoting(sessionId: string) {
    return this.api.post(`/voting/sessions/${sessionId}/end`);
  }

  async castVote(voteData: any) {
    return this.api.post('/voting/vote', voteData);
  }

  async getVotingResults(sessionId: string) {
    return this.api.get(`/voting/sessions/${sessionId}/results`);
  }

  async getVotingLiveStats(sessionId: string) {
    return this.api.get(`/voting/sessions/${sessionId}/live-stats`);
  }

  async hasUserVoted(sessionId: string, userId: string) {
    return this.api.get(`/voting/sessions/${sessionId}/has-voted`, { params: { userId } });
  }

  async getUserVotedStatusBatch(sessionIds: string[]) {
    return this.api.post('/voting/sessions/vote-status-batch', { sessionIds });
  }

  // Métodos de agenda
  async createAgendaItem(agendaData: any) {
    return this.api.post('/agenda/items', agendaData);
  }

  async updateAgendaItem(itemId: string, agendaData: any) {
    return this.api.put(`/agenda/items/${itemId}`, agendaData);
  }

  async deleteAgendaItem(itemId: string) {
    return this.api.delete(`/agenda/items/${itemId}`);
  }

  async getAgendaItem(itemId: string) {
    return this.api.get(`/agenda/items/${itemId}`);
  }

  async getAgendaItemsByAssembly(assemblyId: string) {
    return this.api.get(`/agenda/assemblies/${assemblyId}/items`);
  }

  async getAgendaItemsManagerView(assemblyId: string) {
    return this.api.get(`/agenda/assemblies/${assemblyId}/items/manager-view`);
  }

  async getAgendaItemsVotingView(assemblyId: string) {
    return this.api.get(`/agenda/assemblies/${assemblyId}/items/voting-view`);
  }

  // Métodos de actas
  async getAssemblyMinutes(assemblyId: string) {
    return this.api.get(`/assemblies/${assemblyId}/minutes`);
  }

  async generateMinutes(assemblyId: string) {
    return this.api.post(`/assemblies/${assemblyId}/minutes/generate`);
  }

  async signMinutes(assemblyId: string, signature: string) {
    return this.api.post(`/assemblies/${assemblyId}/minutes/sign`, { signature });
  }

  async downloadMinutes(assemblyId: string) {
    return this.api.get(`/assemblies/${assemblyId}/minutes/download`, {
      responseType: 'blob'
    });
  }

  // Métodos de chat
  async getChatMessages(assemblyId: string, params?: any) {
    return this.api.get(`/assemblies/${assemblyId}/chat`, { params });
  }

  async sendChatMessage(assemblyId: string, message: string, type = 'public') {
    return this.api.post(`/assemblies/${assemblyId}/chat`, { message, type });
  }

  // Métodos de reportes
  async getAssemblyStats(assemblyId?: string) {
    const endpoint = assemblyId ? `/reports/assembly/${assemblyId}` : '/reports/dashboard';
    return this.api.get(endpoint);
  }

  async getParticipationReport(params?: any) {
    return this.api.get('/reports/participation', { params });
  }

  async getQuorumReport(params?: any) {
    return this.api.get('/reports/quorum', { params });
  }

  // Métodos de carga masiva de usuarios
  async bulkUploadUsers(formData: FormData) {
    return this.api.post('/admin/users/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 minutos para archivos grandes
    });
  }

  async previewBulkUpload(formData: FormData) {
    return this.api.post('/admin/users/preview-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30 segundos para preview
    });
  }

  async sendBulkInvitations(invitations: any[]) {
    return this.api.post('/admin/users/send-invitations', { invitations }, {
      timeout: 120000, // 2 minutos para envío masivo de emails
    });
  }

  async downloadUserTemplate() {
    return this.api.get('/admin/users/template', {
      responseType: 'blob',
    });
  }

  async resendInvitation(userEmail: string, forceResend: boolean = false) {
    return this.api.post(`/admin/users/resend-invitation`, { 
      email: userEmail,
      forceResend
    });
  }

  async getEmailDeliveryDiagnostics(params?: { windowHours?: number; type?: 'welcome' | 'assembly' | 'delegation' | 'other' }) {
    return this.api.get('/admin/diagnostics/email-delivery', { params });
  }
}

export const apiService = new ApiService();
export default apiService;